import { Tile } from '@components/shared/tile.component';
import { AlertCircleIcon, ArrowRightIcon, ClipboardListIcon, StarIcon } from 'lucide-react';
import { FC, useMemo } from 'react';

type UseWithoutAssessment = {
  domainName: string;
  useName: string;
};

type RecommendedActionsTileProps = {
  hasDraftSurvey: boolean;
  hasFeaturedAssessment: boolean;
  usesWithoutAssessments: UseWithoutAssessment[];
};

type ActionItemProps = {
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  title: string;
};

const ActionItem: FC<ActionItemProps> = (props) => {
  const { description, icon, onClick, title } = props;

  const content = (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="shrink-0 text-primary-600">{icon}</div>
      <div className="flex flex-1 flex-col">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <span className="text-xs text-gray-500">{description}</span>
      </div>
      {onClick && <ArrowRightIcon className="size-4 shrink-0 text-gray-400" />}
    </div>
  );

  if (onClick) {
    return (
      <button
        className="w-full cursor-pointer text-left transition-colors hover:opacity-80"
        onClick={onClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  return content;
};

export const RecommendedActionsTile: FC<RecommendedActionsTileProps> = (props) => {
  const { hasDraftSurvey, hasFeaturedAssessment, usesWithoutAssessments } = props;

  const actions = useMemo(() => {
    const items: ActionItemProps[] = [];

    // Suggest completing draft survey
    if (hasDraftSurvey) {
      items.push({
        description: 'Complete your draft survey to update your TRL score',
        icon: <ClipboardListIcon className="size-5" />,
        title: 'Complete Survey',
      });
    }

    // Suggest creating primary TRL if none exists
    if (!hasFeaturedAssessment) {
      items.push({
        description: 'Create your first TRL assessment to establish a baseline',
        icon: <StarIcon className="size-5" />,
        title: 'Create Primary TRL',
      });
    }

    // Suggest assessments for use+domain combinations without data
    usesWithoutAssessments.forEach(({ domainName, useName }) => {
      items.push({
        description: `${useName} (${domainName}) needs assessment`,
        icon: <AlertCircleIcon className="size-5" />,
        title: 'Missing Assessment',
      });
    });

    return items;
  }, [hasDraftSurvey, hasFeaturedAssessment, usesWithoutAssessments]);

  // Hide tile if no actions to recommend
  if (actions.length === 0) {
    return null;
  }

  return (
    <Tile header={{ title: 'Recommended Actions' }}>
      <div className="flex flex-col gap-2">
        {actions.map((action, index) => (
          <ActionItem
            key={index}
            {...action}
          />
        ))}
      </div>
    </Tile>
  );
};
